import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Alert,
  InputAdornment,
  Autocomplete,
  Box,
  Chip,
  CircularProgress,
  Typography,
} from '@mui/material';
import { FloatLoadRequest, FloatLoadType, FloatLoadTypeLabels } from '../types/account';
import { accountService, partnerService } from '../services/api';

interface Partner {
  id: string;
  partnerCode: string;
  partnerName: string;
  partnerType: 'MERCHANT' | 'RETAILER' | 'AGENT';
  partnerStatus: string;
}

interface FloatLoadDialogProps {
  open: boolean;
  partnerId?: string;
  onClose: () => void;
  onSuccess: () => void;
}

const FloatLoadDialog: React.FC<FloatLoadDialogProps> = ({
  open,
  partnerId: initialPartnerId,
  onClose,
  onSuccess,
}) => {
  const [formData, setFormData] = useState<FloatLoadRequest>({
    partnerId: initialPartnerId || '',
    currency: 'USD',
    amount: 0,
    loadType: FloatLoadType.BANK_TRANSFER,
    referenceNumber: '',
    bankReference: '',
    depositDate: new Date().toISOString().split('T')[0],
    notes: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Partner search state
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartner, setSelectedPartner] = useState<Partner | null>(null);
  const [partnerSearchLoading, setPartnerSearchLoading] = useState(false);
  const [partnerSearchTerm, setPartnerSearchTerm] = useState('');

  // Search partners when search term changes
  useEffect(() => {
    const searchPartners = async () => {
      if (partnerSearchTerm.length < 2) {
        setPartners([]);
        return;
      }

      try {
        setPartnerSearchLoading(true);
        // Float can only be loaded for retailers: agent float is managed in the
        // external float system and merchants are settled, not floated.
        const response = await partnerService.getPartners({
          search: partnerSearchTerm,
          partnerStatus: 'ACTIVE',
          partnerType: 'RETAILER',
          size: 20,
        });
        setPartners(response.content || []);
      } catch (err) {
        console.error('Failed to search partners:', err);
      } finally {
        setPartnerSearchLoading(false);
      }
    };

    const delayDebounce = setTimeout(() => {
      searchPartners();
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [partnerSearchTerm]);

  // Load initial partner if partnerId is provided
  useEffect(() => {
    const loadInitialPartner = async () => {
      if (initialPartnerId && !selectedPartner) {
        try {
          const partner = await partnerService.getPartner(initialPartnerId);
          setSelectedPartner(partner);
        } catch (err) {
          console.error('Failed to load partner:', err);
        }
      }
    };

    loadInitialPartner();
  }, [initialPartnerId]);

  const handleSubmit = async () => {
    if (selectedPartner?.partnerType === 'AGENT') {
      setError('Float cannot be loaded for agents: agent float is managed in the external float system.');
      return;
    }
    try {
      setLoading(true);
      setError(null);
      await accountService.loadFloat(formData);
      onSuccess();
      onClose();
      resetForm();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load float');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      partnerId: initialPartnerId || '',
      currency: 'USD',
      amount: 0,
      loadType: FloatLoadType.BANK_TRANSFER,
      referenceNumber: '',
      bankReference: '',
      depositDate: new Date().toISOString().split('T')[0],
      notes: '',
    });
    setError(null);
    if (!initialPartnerId) {
      setSelectedPartner(null);
      setPartnerSearchTerm('');
    }
  };

  const handlePartnerChange = (event: any, newValue: Partner | null) => {
    setSelectedPartner(newValue);
    setFormData({ ...formData, partnerId: newValue?.id || '' });
  };

  const getPartnerTypeColor = (type: string): 'primary' | 'secondary' | 'success' => {
    switch (type) {
      case 'MERCHANT':
        return 'primary';
      case 'RETAILER':
        return 'secondary';
      case 'AGENT':
        return 'success';
      default:
        return 'primary';
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Load Float</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2} sx={{ mt: 1 }}>
          <Grid item xs={12}>
            <Autocomplete
              fullWidth
              options={partners}
              value={selectedPartner}
              onChange={handlePartnerChange}
              onInputChange={(event, newInputValue) => {
                setPartnerSearchTerm(newInputValue);
              }}
              getOptionLabel={(option) =>
                `${option.partnerName} (${option.partnerCode})`
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Box sx={{ flexGrow: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{option.partnerName}</span>
                      <Chip
                        label={option.partnerType}
                        size="small"
                        color={getPartnerTypeColor(option.partnerType)}
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {option.partnerCode}
                    </Typography>
                  </Box>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Retailer"
                  required
                  placeholder="Search by retailer name or code..."
                  helperText="Float is loaded for retailers only. Agent float is managed in the external float system."
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {partnerSearchLoading ? <CircularProgress size={20} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
              disabled={!!initialPartnerId}
              loading={partnerSearchLoading}
              noOptionsText={
                partnerSearchTerm.length < 2
                  ? 'Type at least 2 characters to search'
                  : 'No partners found'
              }
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <FormControl fullWidth required>
              <InputLabel>Currency</InputLabel>
              <Select
                value={formData.currency}
                label="Currency"
                onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
              >
                <MenuItem value="USD">USD</MenuItem>
                <MenuItem value="ZWG">ZWG</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Amount"
              type="number"
              value={formData.amount}
              onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
              required
              InputProps={{
                startAdornment: <InputAdornment position="start">{formData.currency === 'USD' ? '$' : 'ZWG'}</InputAdornment>,
              }}
            />
          </Grid>
          <Grid item xs={12}>
            <FormControl fullWidth required>
              <InputLabel>Load Type</InputLabel>
              <Select
                value={formData.loadType}
                label="Load Type"
                onChange={(e) => setFormData({ ...formData, loadType: e.target.value as FloatLoadType })}
              >
                {Object.values(FloatLoadType).map((type) => (
                  <MenuItem key={type} value={type}>
                    {FloatLoadTypeLabels[type]}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Reference Number"
              value={formData.referenceNumber}
              onChange={(e) => setFormData({ ...formData, referenceNumber: e.target.value })}
              required
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Bank Reference"
              value={formData.bankReference}
              onChange={(e) => setFormData({ ...formData, bankReference: e.target.value })}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Deposit Date"
              type="date"
              value={formData.depositDate}
              onChange={(e) => setFormData({ ...formData, depositDate: e.target.value })}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              multiline
              rows={3}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" disabled={loading}>
          {loading ? 'Loading...' : 'Load Float'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default FloatLoadDialog;
